import React, { useRef, useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { useDimensions } from "common/hooks";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    width: 100%;
    height: 100%;

    text {
      &.label {
        fill: var(--chart-axis-text-color);
        color: var(--chart-axis-text-color);
        font-size: 11px;
        font-family: "AllianceNo2-Regular" !important;
      }
        &.value {
        fill: white;
        color: white;
        font-size: 11px;
        font-family: "AllianceNo2-SemiBold" !important;
        }
      &.value.outside {
        fill: var(--chart-axis-text-color);
        color: var(--chart-axis-text-color);
        }
    }
  }
`;

const BAR_PADDING = 0.2;
const MARGIN = { top: 30, right: 30, bottom: 30, left: 80 };

const LABEL_GAP = 3;

const LABEL_FONT = '11px "AllianceNo2-Regular", sans-serif';

const VALUE_FONT = '12px "AllianceNo2-SemiBold", sans-serif';

const VALUE_GAP = 7;

const ELLIPSIS = "\u2026";

const measureText = (() => {
  let context;

  return (text, font) => {
    if (context === undefined) {
      context = document.createElement("canvas").getContext("2d");
    }

    if (context === null) {
      return null;
    }

    context.font = font;

    return context.measureText(text).width;
  };
})();

const measureLabel = (text) => measureText(text, LABEL_FONT);

const widthValue = (value) => {
  const width = measureText(`${value}`, VALUE_FONT);

  return width === null ? null : width + 2 * VALUE_GAP;
};


const nameFitting = (name, width) => {
  const widthName = measureLabel(name);

  if (widthName === null || widthName <= width) {
    return name;
  }

  const doesFit = (kept) => measureLabel(`${kept}${ELLIPSIS}`) <= width;
  let kept = name;

  while (kept.length > 1 && !doesFit(kept)) {
    kept = kept.slice(0, -1);
  }

  return `${kept}${ELLIPSIS}`;
};

function HorizontalBarChart({ data, height, margin = MARGIN, onClick }) {
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);
  const { width } = dimensions;
  const heightChart = Math.max(0, height - margin.top - margin.bottom);
  const widthChart = Math.max(0, width - margin.left - margin.right);
  const colorsSet = login.isTheme("light") ? colors : colorsDark;

  const groups = useMemo(() => data.map((d) => d.name), [data]);

  const widthLabel = Math.max(0, margin.left - LABEL_GAP);
  const namesShown = useMemo(
    () => new Map(groups.map((name) => [name, nameFitting(name, widthLabel)])),
    [groups, widthLabel]
  );

  const yScale = useMemo(() => {
    return d3
      .scaleBand()
      .domain(groups)
      .range([0, heightChart])
      .padding(BAR_PADDING);
  }, [groups, heightChart]);

  const xScale = useMemo(() => {
    const max = d3.max(data, (d) => d.value);
    return d3
      .scaleLinear()
      .domain([0, max || 10])
      .range([0, widthChart]);
  }, [data, widthChart]);

  // Build the shapes
  const allShapes = data.map((d, i) => {
    const y = yScale(d.name);
    if (y === undefined) {
      return null;
    }

    // A value that does not fit inside its bar is drawn at the right of it,
    // so that it is always seen.
    const width = widthValue(d.value);
    const isValueInside = width === null || xScale(d.value) >= width;

    return (
      <g key={d.name} onClick={() => onClick(d.name)}>
        <rect
          x={xScale(0)}
          y={yScale(d.name)}
          width={xScale(d.value)}
          height={yScale.bandwidth()}
          opacity={0.8}
          fill={colorsSet[i % colorsSet.length]}
          fillOpacity={1}
          rx={1}
        />
        <text
          className={`value${isValueInside ? "" : " outside"}`}
          x={
            isValueInside
              ? xScale(d.value) - VALUE_GAP
              : xScale(d.value) + VALUE_GAP
          }
          y={y + yScale.bandwidth() / 2}
          textAnchor={isValueInside ? "end" : "start"}
          dominantBaseline="central"
          fontSize={12}
        >
          {d.value}
        </text>
        <text
          className="label"
          x={xScale(0) - LABEL_GAP}
          y={y + yScale.bandwidth() / 2}
          textAnchor="end"
          dominantBaseline="central"
        >
          <title>{d.name}</title>
          {namesShown.get(d.name)}
        </text>
      </g>
    );
  });

  return (
    <Wrapper>
      <WrapperChart ref={chartRef} style={{ height }}>
        <svg width={width} height={height}>
          <g transform={`translate(${[margin.left, margin.top].join(",")})`}>
            <g className="bars">{allShapes}</g>
          </g>
        </svg>
      </WrapperChart>
    </Wrapper>
  );
}

export default HorizontalBarChart;
